import { RequestCard } from './RequestCard';
import { VerificationCard } from './VerificationCard';
import { CloneModal } from './CloneModal';
import { getRequestKey } from '../utils/requestKey';
import { Card, Button, Tabs, Icon } from '../design-system/components';

/**
 * Request panel component
 * Manages request list display with pagination and view modes
 */
export const RequestPanel = ({
  filteredRequests,
  currentRequests,
  selectedRequest,
  setSelectedRequest,
  requestViewMode,
  setRequestViewMode,
  sortBy,
  setSortBy,
  currentPage,
  setCurrentPage,
  totalPages,
  requestsPerPage,
  onStatusChange,
  onUpdateAdminNotes,
  onClone,
  onUnassign,
  onMarkScheduled,
  onCancel,
  onResendEmail,
  onSetRefereePattern,
  onVerificationConfirm,
  venues,
  cloningRequest,
  setCloningRequest,
  onCloneSubmit
}) => {
  const PaginationControls = () => (
    filteredRequests.length > requestsPerPage ? (
      <div className="flex justify-between items-center" style={{ padding: 'var(--space-3)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' }}>
        <Button
          variant="outline"
          size="sm"
          iconLeft="chevron-left"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          Previous
        </Button>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          Page {currentPage} of {totalPages} ({filteredRequests.length} total requests)
        </span>
        <Button
          variant="outline"
          size="sm"
          iconRight="chevron-right"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        >
          Next
        </Button>
      </div>
    ) : null
  );

  return (
    <Card padding="var(--space-5)" className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-xl)', color: 'var(--text-strong)' }}>
            Requests ({filteredRequests.length})
          </h2>
          <Tabs
            items={[
              { value: 'standard', label: 'Standard' },
              { value: 'verify', label: 'Verify' },
            ]}
            value={requestViewMode}
            onChange={setRequestViewMode}
          />
        </div>
        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-xs)', letterSpacing: 'var(--ls-wide)', color: 'var(--text-muted)' }}>
            Sort by:
          </span>
          <Tabs
            items={[
              { value: 'date', label: 'Date' },
              { value: 'gameNumber', label: 'Game #' },
            ]}
            value={sortBy}
            onChange={setSortBy}
          />
        </div>
      </div>

      {/* Pagination Top */}
      <PaginationControls />

      {/* Request List */}
      <div className="flex-1 overflow-y-auto space-y-3 my-4">
        {currentRequests.length === 0 ? (
          <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
            <Icon name="clipboard" size={40} color="var(--text-muted)" style={{ margin: '0 auto var(--space-2)' }} />
            <div>No requests match your filters</div>
          </div>
        ) : requestViewMode === 'verify' ? (
          // currentRequests is already Processing-only in verify mode (App.jsx
          // filters before paginating), so no extra filter needed here.
          currentRequests
            .map((request) => (
              <VerificationCard
                key={request.RowIndex || getRequestKey(request)}
                request={request}
                isSelected={
                  !!selectedRequest &&
                  getRequestKey(selectedRequest) === getRequestKey(request)
                }
                onSelect={() => setSelectedRequest(request)}
                onConfirm={onVerificationConfirm}
              />
            ))
        ) : (
          currentRequests.map((request) => {
            // Calculate stale status
            const status = request['Request Status'];
            const lastUpdated = request['Last Updated'] || request.Timestamp;
            let daysInProcessing = 0;
            let isStale = false;

            if (status === 'Processing' && lastUpdated) {
              const now = new Date();
              const updated = new Date(lastUpdated);
              daysInProcessing = Math.floor((now - updated) / (1000 * 60 * 60 * 24));
              isStale = daysInProcessing >= 5;
            }

            return (
              <RequestCard
                key={request.RowIndex || getRequestKey(request)}
                request={request}
                isStale={isStale}
                daysInProcessing={daysInProcessing}
                isSelected={
                  !!selectedRequest &&
                  getRequestKey(selectedRequest) === getRequestKey(request)
                }
                onSelect={() => setSelectedRequest(request)}
                onStatusChange={onStatusChange}
                onUpdateAdminNotes={onUpdateAdminNotes}
                onClone={onClone}
                onUnassign={onUnassign}
                onMarkScheduled={onMarkScheduled}
                onCancel={onCancel}
                onResendEmail={onResendEmail}
                onSetRefereePattern={onSetRefereePattern}
                requestViewMode={requestViewMode}
              />
            );
          })
        )}
      </div>

      {/* Pagination Bottom */}
      <PaginationControls />

      {/* Clone Modal */}
      {cloningRequest && (
        <CloneModal
          cloningRequest={cloningRequest}
          venues={venues}
          onSubmit={onCloneSubmit}
          onCancel={() => setCloningRequest(null)}
        />
      )}
    </Card>
  );
};
